import type { Page } from '@cng/shared';

interface OfferStripItem {
  badge?: string;
  text?: string;
  sub?: string;
  price?: string;
}

interface OfferStripData {
  label?: string;
  separator?: string;
  speedSeconds?: number;
  items?: OfferStripItem[];
}

/**
 * The "Special Offers" marquee above the women's price menu. Content comes from
 * the page's `ticker` section, so the owner edits the promotion rather than the
 * markup. Items are duplicated once for the seamless -50% scroll, as on the
 * original.
 */
export function OfferStrip({ page }: { page: Page }) {
  const section = page.sections.find((s) => s.type === 'ticker' && s.enabled);
  if (!section) return null;

  const data = section.data as OfferStripData;
  const items = data.items ?? [];
  if (items.length === 0) return null;

  const separator = data.separator ?? '• • •';
  const rendered = [...items, ...items];

  return (
    <div className="cg-ticker-outer" role="region" aria-label={data.label || 'Special offers'}>
      <div className="cg-ticker-label">
        <div className="cg-dot" aria-hidden="true" />
        <span className="cg-label-text">{data.label || 'Special Offers'}</span>
      </div>
      <div className="cg-fade-l" aria-hidden="true" />
      <div className="cg-fade-r" aria-hidden="true" />
      <div className="cg-ticker-inner">
        <div
          className="cg-ticker-track"
          style={data.speedSeconds ? { animationDuration: `${data.speedSeconds}s` } : undefined}
        >
          {rendered.map((item, i) => (
            <span key={`${item.text}-${i}`} aria-hidden={i >= items.length || undefined}>
              <span className="cg-offer-tag">
                {item.badge ? <span className="cg-badge">{item.badge}</span> : null}
                <span className="cg-offer-text">
                  {item.text}
                  {item.sub ? <span className="cg-offer-sub"> {item.sub}</span> : null}
                </span>
                {item.price ? <span className="cg-price">{item.price}</span> : null}
              </span>
              <span className="cg-sep" aria-hidden="true">{separator}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
